import { TAU, WHITE, circle, glow, rim } from '../paint';
import type { Ctx, Piece } from '../paint';
import { REFERENCE_RADIUS } from '../textures-looks';

export const UNIT = 2;

const MOSS = '#2f6b3a';
const CAPE_LEAVES = ['#7cc85c', '#5bb85c'] as const;
const HOOD_LEAVES = ['#a6dc6a', '#6fbf5a'] as const;
const BRASS = '#c9a05a';
const GLASS = 'rgba(255,240,180,.25)';
const OUT = 'rgba(4,6,18,.9)';

export const CAPE = { along: -2, width: 11, height: 15.5, leaves: 10, leafAt: [9.5, 13] } as const;
const TORSO = { width: 7.5, height: 12 } as const;
export const HOOD = { along: 2, radius: 7.4, leaves: 9, leafAt: 6.4 } as const;
export const HANDS = { free: [10, -10], holding: [12, 8] } as const;
export const LANTERN = { along: 17, across: 8.5, half: 4, glow: 16 } as const;

function units(draw: (ctx: Ctx) => void): (ctx: Ctx) => void {
  return (ctx) => {
    ctx.save();
    ctx.scale(UNIT, UNIT);
    draw(ctx);
    ctx.restore();
  };
}

function fillRim(ctx: Ctx, fill: string, width: number): void {
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = width;
  ctx.strokeStyle = OUT;
  ctx.stroke();
}

function leaf(ctx: Ctx, x: number, y: number, turn: number, fill: string): void {
  ctx.beginPath();
  ctx.ellipse(x, y, 2.6, 1.3, turn, 0, TAU);
  fillRim(ctx, fill, 0.6);
}

function capePath(ctx: Ctx): void {
  ctx.beginPath();
  ctx.ellipse(0, 0, CAPE.width, CAPE.height, 0, 0, TAU);
}

function cape(ctx: Ctx): void {
  capePath(ctx);
  fillRim(ctx, MOSS, 2);
  const [alongAt, acrossAt] = CAPE.leafAt;
  for (let index = 0; index < CAPE.leaves; index += 1) {
    const angle = Math.PI / 2 + (index / (CAPE.leaves - 1)) * Math.PI;
    leaf(
      ctx,
      Math.cos(angle) * alongAt,
      Math.sin(angle) * acrossAt,
      angle,
      CAPE_LEAVES[index % 2] ?? MOSS,
    );
  }
}

function silhouette(ctx: Ctx): void {
  capePath(ctx);
  ctx.fillStyle = WHITE;
  ctx.fill();
}

function torso(ctx: Ctx): void {
  ctx.beginPath();
  ctx.ellipse(0, 0, TORSO.width, TORSO.height, 0, 0, TAU);
  ctx.fillStyle = '#e4e4e4';
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(1.5, 0, TORSO.width / 2, TORSO.height * 0.66, 0, 0, TAU);
  ctx.fillStyle = WHITE;
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(0, 0, TORSO.width, TORSO.height, 0, 0, TAU);
  rim(ctx, 2);
}

function hand(ctx: Ctx): void {
  circle(ctx, 3.2);
  ctx.fillStyle = WHITE;
  ctx.fill();
  rim(ctx, 1.3);
}

function hood(ctx: Ctx): void {
  circle(ctx, HOOD.radius);
  fillRim(ctx, MOSS, 1.5);
  for (let index = 0; index < HOOD.leaves; index += 1) {
    const angle = (index * TAU) / HOOD.leaves;
    leaf(
      ctx,
      Math.cos(angle) * HOOD.leafAt,
      Math.sin(angle) * HOOD.leafAt,
      angle + Math.PI / 2,
      HOOD_LEAVES[index % 2] ?? MOSS,
    );
  }
}

function lantern(ctx: Ctx): void {
  const half = LANTERN.half;
  ctx.beginPath();
  ctx.roundRect(-half, -half, 2 * half, 2 * half, 2);
  ctx.fillStyle = GLASS;
  ctx.fill();
  ctx.lineWidth = 1.3;
  ctx.strokeStyle = BRASS;
  ctx.stroke();
}

function spark(ctx: Ctx): void {
  glow(ctx, WHITE, 2);
  circle(ctx, 1.8);
  ctx.fillStyle = WHITE;
  ctx.fill();
}

function soft(width: number, height: number): Piece {
  return [
    96,
    96,
    REFERENCE_RADIUS,
    (ctx) => {
      glow(ctx, WHITE, 8);
      ctx.fillStyle = WHITE;
      ctx.beginPath();
      ctx.ellipse(0, 0, width, height, 0, 0, TAU);
      ctx.fill();
    },
  ];
}

export const VEILLEUR_PIECES = {
  cape: [56, 70, REFERENCE_RADIUS, units(cape)],
  silhouette: [48, 66, REFERENCE_RADIUS, units(silhouette)],
  torso: [36, 52, REFERENCE_RADIUS, units(torso)],
  hand: [16, 16, REFERENCE_RADIUS, units(hand)],
  hood: [40, 40, REFERENCE_RADIUS, units(hood)],
  lantern: [22, 22, REFERENCE_RADIUS, units(lantern)],
  spark: [10, 10, REFERENCE_RADIUS, spark],
  shadow: soft(22, 28),
} satisfies Record<string, Piece>;
